import { supabase } from "@/integrations/supabase/client";

/**
 * Uploads a DataURL (base64) image to Supabase Storage and returns the public URL
 */
export const uploadDataUrlToStorage = async (
  dataUrl: string,
  bucket: string,
  path: string
): Promise<string | null> => {
  try {
    // 1. Extract base64 data and mime type
    const matches = dataUrl.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,(.+)$/);
    if (!matches) {
      console.error('Invalid DataURL format');
      return null;
    }
    
    const mimeType = matches[1];
    const base64Data = matches[2];
    
    // 2. Convert base64 to Uint8Array
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // 3. Create Blob
    const blob = new Blob([bytes], { type: mimeType });
    
    // 4. Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, blob, {
        contentType: mimeType,
        upsert: true,
      });
    
    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }
    
    // 5. Get public URL
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  } catch (error) {
    console.error('Error uploading DataURL:', error);
    return null;
  }
};

/**
 * Uploads multiple logo variations to storage and returns their public URLs
 */
export const uploadLogoVariations = async (
  userId: string,
  brandKitId: string,
  variations: {
    colorida?: string;
    preto?: string;
    neutroClaro?: string;
    tonsNeutros?: string;
  }
): Promise<{
  logo_color_transparent_url?: string;
  logo_black_transparent_url?: string;
  logo_neutral_light_transparent_url?: string;
  logo_neutral_tones_url?: string;
}> => {
  const timestamp = Date.now();
  const basePath = `brand-logos/${userId}/${brandKitId}`;
  const results: Record<string, string | undefined> = {};
  
  const uploads = await Promise.all([
    // Colorida (transparent)
    variations.colorida
      ? uploadDataUrlToStorage(variations.colorida, 'temp_logos', `${basePath}/colorida_${timestamp}.png`)
      : Promise.resolve(null),
    // Preto 100% (silhouette)
    variations.preto
      ? uploadDataUrlToStorage(variations.preto, 'temp_logos', `${basePath}/preto_${timestamp}.png`)
      : Promise.resolve(null),
    // Neutro Claro (dark logo)
    variations.neutroClaro
      ? uploadDataUrlToStorage(variations.neutroClaro, 'temp_logos', `${basePath}/neutro_claro_${timestamp}.png`)
      : Promise.resolve(null),
    // Tons Neutros (inverted/white)
    variations.tonsNeutros
      ? uploadDataUrlToStorage(variations.tonsNeutros, 'temp_logos', `${basePath}/tons_neutros_${timestamp}.png`)
      : Promise.resolve(null),
  ]);
  
  return {
    logo_color_transparent_url: uploads[0] || undefined,
    logo_black_transparent_url: uploads[1] || undefined,
    logo_neutral_light_transparent_url: uploads[2] || undefined,
    logo_neutral_tones_url: uploads[3] || undefined,
  };
};
